import { createApi } from '@reduxjs/toolkit/query/react';
import { t } from 'i18next';

import {
  CleaningListResponse,
  MonitoredDepotDto,
  MonitoredDepotsResponse,
  NextPlannedDeparturesResponse,
  WashHistoryQueryArgs,
  WashHistoryResponse,
  WashVehiclesQueryArgs,
} from '@/apps/washing/types/IWashing';
import { buildWashHistoryQueryParams } from '@/apps/washing/utils/buildWashHistoryQueryParams';
import { buildWashQueryParams } from '@/apps/washing/utils/buildWashQueryParams';
import { addNotification } from '@/redux/slices/notificationSlice';
import { axiosBaseQuery } from '@/redux/axios';
import { getRtkErrorStatus } from '@/redux/rtkQueryErrors';
import { apiRoutes } from '@/api/apiRoutes';

// Factory rather than a bare singleton so tests can build an isolated instance
// per case (the `wasFailing` flag below is intentionally shared across polls of
// the *same* production instance, but must not leak between test cases).
export const createWashingApi = () => {
  // RTK Query applies the pending-status reducer for a new attempt before
  // invoking onQueryStarted, so by the time this callback runs, getCacheEntry()
  // already reports the *new* request (status 'pending'), never the previous
  // one's outcome. Tracking the last outcome here, alongside the actual
  // queryFulfilled settle, is what makes a success→failure transition
  // detectable — not a synchronous read of the cache entry.
  let wasFailing = false;

  return createApi({
    reducerPath: 'washingApi',
    baseQuery: axiosBaseQuery(),
    tagTypes: [
      'CleaningList',
      'NextDepartures',
      'MonitoredDepots',
      'WashHistory',
    ],
    endpoints: (builder) => ({
      getCleaningList: builder.query<
        CleaningListResponse,
        WashVehiclesQueryArgs | void
      >({
        query: (args) => ({
          url: apiRoutes.getWashCleaningList.path(),
          method: apiRoutes.getWashCleaningList.method,
          params: buildWashQueryParams(args),
        }),
        providesTags: ['CleaningList'],
        onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
          try {
            await queryFulfilled;
            wasFailing = false;
          } catch (caught) {
            // queryFulfilled rejects with the lifecycle envelope, so the
            // normalized { status, data } from axiosBaseQuery is one level in.
            const rtkError = (
              caught as {
                error?: { status?: number; data?: { detail?: string } };
              }
            ).error;

            // 403 is a state, not a fault: the caller has SeeWashing but no
            // granted contract (or asked for a depot outside its grant). The
            // dashboard renders a dedicated empty state for it, so a toast
            // would only add noise — and it must not arm the dedup flag
            // either, or a later real outage would go unreported.
            if (getRtkErrorStatus(rtkError) === 403) {
              return;
            }

            // Only toast on the success→failure transition — a sustained
            // outage on an unattended depot screen would otherwise queue one
            // identical notification every poll (~30s).
            if (wasFailing) {
              return;
            }
            wasFailing = true;

            dispatch(
              addNotification({
                type: 'error',
                message: t('washing.load_failed'),
                description: rtkError?.data?.detail,
              })
            );
          }
        },
      }),
      // No consumer and no notification handler: the cleaning-list rows
      // already carry nextPlannedDepartureAt, so the dashboard does not call
      // this, and there is no screen a toast could belong to.
      getNextPlannedDepartures: builder.query<
        NextPlannedDeparturesResponse,
        WashVehiclesQueryArgs | void
      >({
        query: (args) => ({
          url: apiRoutes.getWashNextDepartures.path(),
          method: apiRoutes.getWashNextDepartures.method,
          params: buildWashQueryParams(args),
        }),
        providesTags: ['NextDepartures'],
      }),
      // The depot picker's option source. Silent on failure for the same
      // reason as above: a 403 here means "no granted contract", which the
      // dashboard shows as an empty state.
      getMonitoredDepots: builder.query<MonitoredDepotDto[], void>({
        query: () => ({
          url: apiRoutes.getWashMonitoredDepots.path(),
          method: apiRoutes.getWashMonitoredDepots.method,
        }),
        transformResponse: (response: MonitoredDepotsResponse) =>
          response.depots,
        providesTags: ['MonitoredDepots'],
      }),
      // No toast: the history page shows its failures inline, and it is not
      // polled, so there is no stream of repeats to dedupe.
      getWashHistory: builder.query<WashHistoryResponse, WashHistoryQueryArgs>({
        query: (args) => ({
          url: apiRoutes.getWashHistory.path(),
          method: apiRoutes.getWashHistory.method,
          params: buildWashHistoryQueryParams(args),
        }),
        providesTags: ['WashHistory'],
      }),
    }),
  });
};

export const washingApi = createWashingApi();
export const {
  useGetCleaningListQuery,
  useGetNextPlannedDeparturesQuery,
  useGetMonitoredDepotsQuery,
  useGetWashHistoryQuery,
} = washingApi;
export default washingApi;
